<template>
  <div class="notification" :class="kind" @click="click">
    <template v-for="chunk in message">
      <!-- Shouldn't be a problem to use the index in this context (non-reactive list, other elements have their own keys) -->
      <!-- TODO(front): use this class to display an icon -->
      <span
        v-if="chunk.icon"
        :key="`${chunk.icon}-${message}`"
        :class="chunk.icon"
      />

      <span v-if="chunk.text" :key="chunk.text">{{ chunk.text }}</span>
      <em v-if="chunk.highlight" :key="chunk.highlight">{{
        chunk.highlight
      }}</em>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useRouter } from "vue-router";

import {
  NotificationChunk,
  Notification as NotificationSchema,
} from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import { navigateToInternalPath } from "@/utils/navigate";

// Helper function to find a link to navigate to from notification chunks
function findLink(
  chunks: NotificationChunk[]
): { external: boolean; link: string } | null {
  const linkCandidates = chunks
    .map((chunk) => {
      if (
        typeof chunk === "string" ||
        "highlight" in chunk ||
        "icon" in chunk
      ) {
        return null;
      }

      if ("event" in chunk) {
        const event = chunk.event;
        if ("recurrence" in event) {
          // This is a recurrent event
          return { external: false, link: `/events-rec/${event.id}` };
        } else {
          // Regular event
          return { external: false, link: `/events/${event.id}` };
        }
      }

      if ("name" in chunk && "uri" in chunk) {
        const { uri } = chunk;
        return {
          external: !!uri.match(/^https?:\/\//),
          link: uri,
        };
      }

      if ("page" in chunk) {
        return { external: false, link: `/pages/${chunk.page}` };
      }

      if ("people" in chunk) {
        return { external: false, link: `/person/${chunk.people}` };
      }

      if ("task" in chunk) {
        const task = chunk.task;
        if ("recurrence" in task) {
          return { external: false, link: `/recurrent-tasks/${task.id}` };
        } else if (task.url) {
          return { external: true, link: task.url };
        } else {
          return { external: false, link: `/tasks/${task.id}` };
        }
      }

      return null;
    })
    .filter(
      (link): link is { external: boolean; link: string } => link !== null
    );

  if (linkCandidates.length === 0) {
    return null;
  }

  if (linkCandidates.length > 1) {
    // eslint-disable-next-line no-console
    console.warn("Multiple links found in notification:", chunks);
  }

  return linkCandidates[0];
}

export default defineComponent({
  name: "Notification",

  props: {
    notification: {
      type: Object as PropType<NotificationSchema>,
      required: true,
    },
  },

  emits: ["close"],

  setup(props, { emit }) {
    const router = useRouter();
    const controller = inject.controller();
    const ERROR_PREFIX = "💀 Unexpected error: ";

    // Compute notification type class
    const kind = computed<string | null>(() => {
      const type = props.notification.type;
      if (type === "error") return "error";
      if (type === "warning") return "warning";
      return null;
    });

    // Process message chunks for rendering
    const message = computed(() => {
      const notification = props.notification;
      const notificationType = notification.type;

      // Add error prefix if it's an error notification
      const messageChunks =
        notificationType === "error"
          ? [{ text: ERROR_PREFIX }, ...notification.message]
          : notification.message;

      // Transform chunks for rendering
      return messageChunks.map((chunk) => {
        if (typeof chunk === "string") {
          return { text: chunk };
        }

        if ("text" in chunk) {
          return { text: chunk.text };
        }

        if ("highlight" in chunk) {
          return { highlight: chunk.highlight };
        }

        if ("icon" in chunk) {
          return { icon: chunk.icon };
        }

        if ("event" in chunk) {
          return { highlight: chunk.event.title };
        }

        if ("name" in chunk && "uri" in chunk) {
          return { highlight: chunk.name };
        }

        if ("page" in chunk) {
          return { highlight: chunk.title };
        }

        if ("people" in chunk) {
          return { highlight: chunk.name };
        }

        if ("task" in chunk) {
          const task = chunk.task;
          if ("recurrence" in task) {
            return { highlight: task.title };
          } else {
            return { highlight: task.title };
          }
        }

        return { text: JSON.stringify(chunk) };
      });
    });

    // Handle click event
    const click = (event: MouseEvent) => {
      const link = findLink(props.notification.message);

      if (!link) {
        emit("close");
        return;
      }

      event.preventDefault();

      if (link.external) {
        window.open(link.link, "_blank");
      } else {
        navigateToInternalPath(controller, router, link.link, event);
      }
    };

    return {
      kind,
      message,
      click,
    };
  },
});
</script>

<style scoped lang="scss">
.notification {
  cursor: default;
  width: fit-content;
  padding: 8px 16px;
  margin: 8px auto;
  font-weight: 500;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  animation: fadeIn 200ms ease-out;
  -webkit-animation: fadeIn 200ms ease-out;
  @include themed() {
    background: t($bg);
    border: 1px solid t($grey-10);
    color: t($grey-70);
  }
  a,
  em {
    cursor: default;
    font-style: normal;
    text-decoration: none;
    @include themed() {
      color: t($secondary);
    }
  }

  &.warning {
    -webkit-animation: none;
    @include themed() {
      background-color: t($secondary-10);
      border: 1px solid t($grey-10);
      color: t($primary);
    }
  }

  &.error {
    -webkit-animation: none;
    color: rgba(245, 244, 241, 0.85);
    @include themed() {
      background-color: t($secondary);
    }
  }

  &:hover {
    @include transition(all 100ms ease-in);
    @include themed() {
      background: rgba(t($bg-alt), 0.9);
    }
  }

  &.error:hover {
    @include themed() {
      background-color: t($secondary); // FIXME darken 10%
    }
  }

  @-webkit-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
}
</style>
